import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { Icon } from './Icon';
import './Onboarding.css';

const Onboarding = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(() => {
    // Try to restore progress from localStorage
    try {
      const saved = localStorage.getItem('onboarding_progress');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if it's recent (within last 24 hours)
        const savedTime = new Date(parsed.timestamp);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        if (hoursDiff < 24 && parsed.step < 6) {
          return parsed.step;
        }
      }
    } catch (e) {
      // Ignore errors
    }
    return 0;
  });
  const [isMinimized, setIsMinimized] = useState(() => {
    // Try to restore minimized state from localStorage
    try {
      const saved = localStorage.getItem('onboarding_minimized');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  
  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Cetarus!',
      description: [
        'We\'re excited to have you here',
        'Let\'s take a quick tour of the platform to help you get started'
      ],
      navigateTo: null
    },
    {
      id: 'explore-protocols',
      title: 'Discover Biohacks',
      description: [
        'Click on any biohack image to see detailed information, research, and user experiences',
        'Press the + button to add biohacks to the protocol builder on the right (desktop) or bottom (mobile)'
      ],
      navigateTo: '/protocols/create'
    },
    {
      id: 'insights',
      title: 'Track Your Progress',
      description: [
        'Create reports on your protocols and health data',
        'View adherence metrics and track biomarker changes over time'
      ],
      navigateTo: '/insights',
      showExampleReport: true
    },
    {
      id: 'create-post',
      title: 'Share Your Journey',
      description: [
        'Create posts to share your biohacking experiences',
        'Share your results and insights with the community'
      ],
      navigateTo: '/home'
    },
    {
      id: 'profile',
      title: 'Your Profile',
      description: [
        'View and edit your profile',
        'See your posts, protocols, and followers',
        'Customize your biohacking journey'
      ],
      navigateTo: '/user'
    },
    {
      id: 'complete',
      title: 'You\'re All Set!',
      description: [
        'You now know the basics of Cetarus',
        'Start exploring, sharing, and tracking your biohacking journey!'
      ],
      navigateTo: null
    }
  ];
  
  // Sync step and minimized state from localStorage when location changes (after navigation)
  // Only run on location changes, not on currentStep changes to avoid loops
  useEffect(() => {
    try {
      const saved = localStorage.getItem('onboarding_progress');
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedTime = new Date(parsed.timestamp);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        // Only update if step is different and valid
        if (hoursDiff < 24 && parsed.step < steps.length) {
          setCurrentStep(prevStep => {
            // Only update if different to prevent loops
            return parsed.step !== prevStep ? parsed.step : prevStep;
          });
        }
      }
      // Restore minimized state only on location change
      const savedMinimized = localStorage.getItem('onboarding_minimized');
      if (savedMinimized === 'true') {
        setIsMinimized(prev => prev ? prev : true);
      }
    } catch (e) {
      // Ignore errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Save progress to localStorage whenever step changes
  useEffect(() => {
    try {
      localStorage.setItem('onboarding_progress', JSON.stringify({
        step: currentStep,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [currentStep]);
  
  // Track if navigation is intentional (part of tour) vs user exit
  const isNavigatingForTour = useRef(false);
  
  // Save minimized state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('onboarding_minimized', isMinimized.toString());
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [isMinimized]);

  // Handle component unmount (navigation away, page close, etc.)
  // Don't mark as exited on normal navigation - only save progress
  useEffect(() => {
    return () => {
      // Save progress but don't mark as exited on normal navigation
      // Only mark as exited if user explicitly closes/skips
      try {
        localStorage.setItem('onboarding_progress', JSON.stringify({
          step: currentStep,
          timestamp: new Date().toISOString()
        }));
        localStorage.setItem('onboarding_minimized', isMinimized.toString());
      } catch (e) {
        // Ignore
      }
      // Reset flag for next time
      if (isNavigatingForTour.current) {
        isNavigatingForTour.current = false;
      }
    };
  }, [currentStep, isMinimized]);
  
  // Handle page unload (browser close)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Save progress and minimized state
      try {
        localStorage.setItem('onboarding_progress', JSON.stringify({
          step: currentStep,
          timestamp: new Date().toISOString()
        }));
        localStorage.setItem('onboarding_minimized', isMinimized.toString());
      } catch (e) {
        // Ignore
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep, isMinimized]);

  const handleNext = () => {
    const validStep = Math.max(0, Math.min(currentStep, steps.length - 1));
    const nextStepIndex = validStep + 1;
    
    if (validStep < steps.length - 1) {
      setCurrentStep(nextStepIndex);
    } else {
      handleComplete();
    }
  };

  const handleTakeMeThere = () => {
    const validStep = Math.max(0, Math.min(currentStep, steps.length - 1));
    const step = steps[validStep];
    
    if (step.navigateTo) {
      // Mark step as seen
      if (step.id === 'explore-protocols') {
        try {
          sessionStorage.setItem('seen_biohacks_onboarding', 'true');
          window.dispatchEvent(new CustomEvent('onboardingProgressUpdate'));
        } catch (e) {
          // Ignore errors
        }
      } else if (step.id === 'insights') {
        try {
          sessionStorage.setItem('seen_insights_onboarding', 'true');
          window.dispatchEvent(new CustomEvent('onboardingProgressUpdate'));
        } catch (e) {
          // Ignore errors
        }
      }
      
      // Save progress - keep current step so onboarding shows on new page
      try {
        localStorage.setItem('onboarding_progress', JSON.stringify({
          step: validStep,
          timestamp: new Date().toISOString()
        }));
        // Set flag to indicate intentional navigation - this prevents the onboarding from being hidden
        localStorage.setItem('onboarding_navigating', 'true');
      } catch (e) {
        // Ignore errors
      }
      
      // Navigate to the page - onboarding will be restored on the new page
      isNavigatingForTour.current = true;
      if (step.showExampleReport) {
        // For insights, navigate and trigger example report
        navigate(step.navigateTo);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('onboardingShowExampleReport'));
        }, 500);
      } else {
        navigate(step.navigateTo);
      }
    }
  };

  const handleSkip = async () => {
    // Clear saved progress
    try {
      localStorage.removeItem('onboarding_progress');
      localStorage.removeItem('onboarding_exited');
      localStorage.removeItem('onboarding_exit_timestamp');
    } catch (e) {
      // Ignore
    }
    await markOnboardingComplete();
    if (onComplete) {
      onComplete();
    }
  };
  
  const handleClose = async () => {
    // Minimize the onboarding - keep it visible but collapsed
    setIsMinimized(true);
    // Save minimized state and progress
    try {
      localStorage.setItem('onboarding_minimized', 'true');
      localStorage.setItem('onboarding_progress', JSON.stringify({
        step: currentStep,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      // Ignore
    }
  };

  const handleExpand = () => {
    setIsMinimized(false);
    // Clear minimized state when user expands
    try {
      localStorage.setItem('onboarding_minimized', 'false');
    } catch (e) {
      // Ignore
    }
  };

  const handleComplete = async () => {
    await markOnboardingComplete();
    if (onComplete) {
      onComplete();
    }
  };

  const markOnboardingComplete = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase
          .from('user_profiles')
          .update({ onboarding_completed: true })
          .eq('user_id', session.user.id);
      }
    } catch (error) {
      console.error('Error marking onboarding as complete:', error);
    }
  };

  // Ensure currentStep is within valid bounds
  const validStep = Math.max(0, Math.min(currentStep, steps.length - 1));
  const currentStepData = steps[validStep];
  const isLastStep = validStep === steps.length - 1;
  const progress = ((validStep + 1) / steps.length) * 100;
  
  // Check if protocol report modal is open by looking for the modal overlay in DOM
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  useEffect(() => {
    const checkModal = () => {
      const modalOverlay = document.querySelector('.report-modal-overlay');
      setIsReportModalOpen(!!modalOverlay);
    };
    
    // Check immediately
    checkModal();
    
    // Set up observer to watch for modal changes
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Also check on location changes
    const interval = setInterval(checkModal, 100);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [location.pathname]);
  
  const shouldUseLowerPosition = isReportModalOpen;

  if (isMinimized) {
    return (
      <div className={`onboarding-guide-container ${shouldUseLowerPosition ? 'onboarding-on-insights' : ''}`}>
        <div className="onboarding-guide onboarding-guide-minimized">
          <div className="onboarding-guide-header">
            <div className="onboarding-guide-title">
              <h3>{currentStepData.title}</h3>
              <span className="onboarding-guide-step-number-minimized">
                Step {validStep + 1} of {steps.length}
              </span>
            </div>
            <button className="onboarding-guide-expand" onClick={handleExpand} aria-label="Expand onboarding">
              <Icon name="ChevronUp" className="icon--sm" />
            </button>
          </div>
          <div className="onboarding-guide-progress-bar-container onboarding-guide-progress-minimized">
            <div 
              className="onboarding-guide-progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`onboarding-guide-container ${shouldUseLowerPosition ? 'onboarding-on-insights' : ''}`}>
      <div className="onboarding-guide">
        <div className="onboarding-guide-header">
          <div className="onboarding-guide-title">
            {validStep === 0 && <Icon name="Info" className="onboarding-guide-icon" />}
            <h3>{currentStepData.title}</h3>
          </div>
          <button className="onboarding-guide-minimize" onClick={handleClose} aria-label="Minimize onboarding">
            <Icon name="ChevronDown" className="icon--sm" />
          </button>
        </div>
        
        <div className="onboarding-guide-content">
          <ul className="onboarding-guide-description">
            {Array.isArray(currentStepData.description) ? (
              currentStepData.description.map((item, index) => (
                <li key={index}>{item}</li>
              ))
            ) : (
              <li>{currentStepData.description}</li>
            )}
          </ul>
          <div className="onboarding-guide-progress-bar-container">
            <div className="onboarding-guide-progress-bar-wrapper">
              <div 
                className="onboarding-guide-progress-bar"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="onboarding-guide-step-number">
              {validStep + 1}/{steps.length}
            </span>
          </div>
        </div>

        <div className="onboarding-guide-actions">
          <div className="onboarding-guide-nav-arrows">
            <button
              className="onboarding-guide-arrow-btn"
              onClick={() => {
                if (validStep > 0) {
                  setCurrentStep(validStep - 1);
                }
              }}
              disabled={validStep === 0}
              aria-label="Previous step"
            >
              <Icon name="ChevronLeft" className="icon--sm" />
            </button>
            <button
              className="onboarding-guide-arrow-btn"
              onClick={handleNext}
              disabled={isLastStep}
              aria-label="Next step"
            >
              <Icon name="ChevronRight" className="icon--sm" />
            </button>
          </div>
          
          <div className="onboarding-guide-buttons">
            {!isLastStep && (
              <button
                type="button"
                className="btn btn--secondary btn--sm onboarding-skip-btn"
                onClick={handleSkip}
              >
                Skip all
              </button>
            )}
            {validStep === 0 && !currentStepData.navigateTo && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={handleNext}
              >
                Next
              </button>
            )}
            {currentStepData.navigateTo && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={handleTakeMeThere}
              >
                {validStep === 2 ? 'See example report' : 'Take me there'}
              </button>
            )}
            {isLastStep && !currentStepData.navigateTo && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={handleComplete}
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
